/**
 * Shared types, components, and constants for AbilityEditor tabs.
 *
 * De-styled (styled-components removed): each former styled component is
 * now a typed React.CSSProperties functional component with the SAME
 * export name + props, so all importers compile unchanged. Transient
 * style props ($active / $variant / $color) keep their names; the style
 * is computed in the component body. Pseudo-classes / scrollbar rules
 * live in the companion `abilityEditorShared.css`.
 *
 * Theme tokens are mapped 1:1 from the prior literals (visual no-op).
 * The accent `#fbbf24` has no exact token (RAW.amber is #ffd23f), so it
 * is kept as a literal, matching the Slider.tsx Phase-0 precedent.
 */

import React from 'react';
import { COLORS, RAW, SPACE, RADII, FONT_SIZE, FONT_WEIGHT } from '../../../theme';
import { AbilityMetadata, abilityRegistry } from '../../../../systems/player/abilities/AbilityRegistry';
import { EventBus } from '../../../../../shared/EventBus';
import './abilityEditorShared.css';

// ── Re-exports for tab convenience ──────────────────────────────
export { abilityRegistry };
export type { AbilityMetadata };

// ── EventBus singleton ──────────────────────────────────────────
export const eventBus = EventBus.getInstance();

// ── Types ───────────────────────────────────────────────────────

export type TabId = 'stats' | 'projectile' | 'behavior' | 'compose' | 'timeline' | 'vfx' | 'particles' | 'preview';

export const TABS: { id: TabId; label: string }[] = [
  { id: 'stats', label: '⚡Stats' },
  { id: 'projectile', label: '🎯Proj' },
  { id: 'behavior', label: '🔧Behav' },
  { id: 'compose', label: '🔗Comp' },
  { id: 'timeline', label: '⏱Time' },
  { id: 'vfx', label: '✨VFX' },
  { id: 'particles', label: '🌟Parts' },
  { id: 'preview', label: '▶Prev' },
];

export const SHAPES = ['rect', 'circle', 'star', 'card', 'arrow', 'crescent', 'spiral', 'beam'] as const;
export const EASINGS = ['Power1', 'Power2', 'Quad.easeInOut', 'Sine.easeInOut', 'Linear', 'Quad.easeIn', 'Quad.easeOut'];
export const PRIM_TYPES = ['circle', 'rect', 'ellipse', 'star', 'arc', 'beam', 'shockwave', 'line'] as const;
export const BEHAVIOR_TYPES = ['projectile', 'multi_projectile', 'aoe', 'channel', 'buff', 'dash', 'heal', 'shield', 'debuff', 'melee', 'instant'] as const;
export const BUFF_STATS = ['attackBonus', 'defenseMultiplier', 'speedMultiplier', 'criticalRate', 'jumpPowerBonus', 'poisonArrowsActive', 'isInvincible', 'isInvisible'] as const;
export const PARTICLE_PRESETS = [
  'hit_spark', 'hit_spark_critical', 'fire_burst', 'ice_burst',
  'lightning_burst', 'arcane_burst', 'healing_particles', 'smoke',
  'sparkle', 'sparkle_burst', 'landing_dust', 'teleport_particles',
] as const;
export const DEFAULT_NEW_PRIMITIVE = {
  color: 0xFFFFFF,
  alpha: 0.7,
  depth: 95,
  tween: { alpha: 0, scale: 1.5, duration: 300, ease: 'Power2' as const },
} as const;

// ── Common tab props ────────────────────────────────────────────

export interface TabProps {
  ability: AbilityMetadata;
  update: (patch: any) => void;
}

export interface PreviewTabProps extends TabProps {
  onCastAbility?: (abilityId: string) => void;
  importJson: string;
  setImportJson: (v: string) => void;
  player: any;
  getVfxId: () => string;
}

export interface VfxTabProps extends TabProps {
  player: any;
  getVfxId: () => string;
  getVfxConfig: () => any;
}

export interface TimelineTabProps extends TabProps {
  player: any;
}

export interface ParticlesTabProps extends TabProps {
  player: any;
}

export interface ComposeTabProps extends TabProps {
  composeSourceId: string;
  setComposeSourceId: (v: string) => void;
}

// ── De-styled components (typed React.CSSProperties) ─────────────
// Accent literal — no 1:1 theme token (RAW.amber is #ffd23f). Kept
// verbatim from the prior styled definitions per Slider.tsx precedent.
// Exported so sibling tabs reference one named constant instead of
// re-typing the raw hex in their inline styles (visual no-op).
export const ACCENT = '#fbbf24';

// Recurring inline-style literals shared across the tab files. None have
// a 1:1 theme token, so they are centralised here (one named source)
// rather than scattered as raw hex — matching the Slider.tsx precedent.
/** Muted field-label grey (form sub-labels). */
export const FIELD_LABEL = '#a0aec0';
/** "Remove / ✕" affordance red. */
export const REMOVE_RED = '#f87171';
/** Faint index/counter grey (#NN markers). */
export const COUNTER_GREY = '#666';

export const TabsContainer: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ style, ...rest }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      flex: 1,
      minHeight: 0,
      ...style,
    }}
    {...rest}
  />
);

export const TabBar: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ style, ...rest }) => (
  <div
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      minHeight: 28,
      borderBottom: `1px solid ${COLORS.borderSubtle}`, // rgba(255,255,255,0.1)
      marginBottom: SPACE.md,
      gap: 1,
      ...style,
    }}
    {...rest}
  />
);

interface TabButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  $active: boolean;
}

export const TabButton: React.FC<TabButtonProps> = ({ $active, style, className, ...rest }) => (
  <button
    className={['ae-tab-button', $active ? 'ae-tab-button--active' : '', className].filter(Boolean).join(' ')}
    style={{
      flex: 1,
      minWidth: 50,
      background: 'none',
      border: 'none',
      borderBottom: `2px solid ${$active ? ACCENT : 'transparent'}`,
      color: $active ? ACCENT : RAW.gray, // #888
      padding: '5px 2px',
      fontSize: FONT_SIZE.xs,
      fontWeight: FONT_WEIGHT.bold,
      textTransform: 'uppercase',
      cursor: 'pointer',
      ...style,
    }}
    {...rest}
  />
);

export const TabContent: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ style, className, ...rest }) => (
  <div
    className={['ae-tab-content', className].filter(Boolean).join(' ')}
    style={{
      flex: 1,
      overflowY: 'auto',
      paddingRight: SPACE.xs,
      ...style,
    }}
    {...rest}
  />
);

export const SelectBox: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ style, className, ...rest }) => (
  <select
    className={['ae-select-box', className].filter(Boolean).join(' ')}
    style={{
      width: '100%',
      background: COLORS.surfaceSunken, // rgba(0,0,0,0.5)
      border: `1px solid ${COLORS.border}`, // rgba(255,255,255,0.2)
      color: COLORS.textPrimary,
      padding: '5px 6px',
      borderRadius: RADII.md,
      fontSize: FONT_SIZE.sm,
      marginBottom: SPACE.md,
      outline: 'none',
      ...style,
    }}
    {...rest}
  />
);

type ActionVariant = 'green' | 'blue' | 'yellow' | 'red';

const ACTION_BG: Record<ActionVariant, string> = {
  green: 'rgba(16,185,129,0.15)',
  blue: 'rgba(59,130,246,0.15)',
  yellow: 'rgba(251,191,36,0.1)',
  red: 'rgba(239,68,68,0.15)',
};
const ACTION_BORDER: Record<ActionVariant, string> = {
  green: 'rgba(16,185,129,0.4)',
  blue: 'rgba(59,130,246,0.4)',
  yellow: 'rgba(251,191,36,0.3)',
  red: 'rgba(239,68,68,0.4)',
};
const ACTION_COLOR: Record<ActionVariant, string> = {
  green: '#6ee7b7',
  blue: '#93c5fd',
  yellow: ACCENT,
  red: '#fca5a5',
};

interface ActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  $variant?: ActionVariant;
}

export const ActionButton: React.FC<ActionButtonProps> = ({ $variant, style, className, ...rest }) => {
  const v = $variant || 'yellow';
  return (
    <button
      className={['ae-action-button', className].filter(Boolean).join(' ')}
      style={{
        width: '100%',
        padding: 7,
        background: ACTION_BG[v],
        border: `1px solid ${ACTION_BORDER[v]}`,
        color: ACTION_COLOR[v],
        borderRadius: RADII.md,
        fontWeight: FONT_WEIGHT.semibold,
        fontSize: FONT_SIZE.sm,
        cursor: 'pointer',
        marginBottom: SPACE.sm,
        ...style,
      }}
      {...rest}
    />
  );
};

export const SectionLabel: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ style, ...rest }) => (
  <div
    style={{
      fontSize: FONT_SIZE.xs,
      fontWeight: FONT_WEIGHT.bold,
      color: '#666', // no 1:1 token (between textMuted/textDisabled); literal kept
      textTransform: 'uppercase',
      margin: '10px 0 6px',
      ...style,
    }}
    {...rest}
  />
);

export const InfoText: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ style, ...rest }) => (
  <div
    style={{
      fontSize: FONT_SIZE.sm,
      color: RAW.gray, // #888
      textAlign: 'center',
      marginTop: SPACE.xl,
      ...style,
    }}
    {...rest}
  />
);

export const TagBadge: React.FC<React.HTMLAttributes<HTMLSpanElement>> = ({ style, ...rest }) => (
  <span
    style={{
      fontSize: 9,
      padding: '2px 5px',
      background: COLORS.surfaceHover, // rgba(255,255,255,0.08)
      borderRadius: RADII.sm,
      color: RAW.silver, // #aaa
      marginRight: SPACE.xs,
      ...style,
    }}
    {...rest}
  />
);

export const PrimitiveRow: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ style, ...rest }) => (
  <div
    style={{
      background: 'rgba(0,0,0,0.2)', // no 1:1 token (overlayScrimLight is 0.3); literal kept
      border: '1px solid rgba(255,255,255,0.06)', // ~borderFaint(0.05); literal kept
      borderRadius: RADII.md,
      padding: SPACE.md,
      marginBottom: SPACE.sm,
      ...style,
    }}
    {...rest}
  />
);

export const PrimitiveHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ style, ...rest }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: SPACE.sm,
      marginBottom: SPACE.sm,
      fontSize: FONT_SIZE.sm,
      color: '#ccc', // no 1:1 token; literal kept
      ...style,
    }}
    {...rest}
  />
);

interface TypeBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  $color?: string;
}

export const TypeBadge: React.FC<TypeBadgeProps> = ({ $color, style, ...rest }) => (
  <span
    style={{
      fontSize: 9,
      padding: '2px 6px',
      background: $color || 'rgba(251,191,36,0.2)',
      borderRadius: RADII.sm,
      color: ACCENT,
      fontWeight: FONT_WEIGHT.bold,
      textTransform: 'uppercase',
      ...style,
    }}
    {...rest}
  />
);

export const ButtonRow: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ style, ...rest }) => (
  <div
    style={{
      display: 'flex',
      gap: SPACE.xs,
      marginBottom: SPACE.sm,
      ...style,
    }}
    {...rest}
  />
);

export const TextArea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = ({ style, className, ...rest }) => (
  <textarea
    className={['ae-textarea', className].filter(Boolean).join(' ')}
    style={{
      width: '100%',
      height: 80,
      background: COLORS.surfaceSunken, // rgba(0,0,0,0.5)
      border: `1px solid ${COLORS.border}`, // rgba(255,255,255,0.2)
      color: '#ddd', // no 1:1 token; literal kept
      padding: SPACE.sm,
      borderRadius: RADII.md,
      fontSize: FONT_SIZE.xs,
      fontFamily: 'monospace',
      resize: 'vertical',
      outline: 'none',
      ...style,
    }}
    {...rest}
  />
);
